import React, { useState, useEffect } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import { 
  Container, Typography, Grid, Card, CardContent, 
  Box, Tab, Tabs, Button, TextField, Alert,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Fade
} from '@mui/material';
import { 
  AccountBalanceWallet, Refresh, Send, SwapHoriz,
  Check, Error, Pending, Settings, TrendingUp, TrendingDown
} from '@mui/icons-material';
import api from '../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: { xs: 1.5, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

function TonIntegration() {
  const [tab, setTab] = usePersistedTab('ton', 0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Депозиты
  const [deposits, setDeposits] = useState([]);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  
  // Выводы
  const [withdrawals, setWithdrawals] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  
  // Курсы обмена
  const [exchangeRate, setExchangeRate] = useState(null);
  const [prevRate, setPrevRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateUpdated, setRateUpdated] = useState(false);
  const [poolInfo, setPoolInfo] = useState(null);
  
  // Настройки
  const [settings, setSettings] = useState({
    minDeposit: 1,
    minWithdrawal: 100,
    withdrawalFee: 1,
    autoProcessDeposits: true,
    autoProcessWithdrawals: false
  });
  
  // Диалог подтверждения вывода
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  useEffect(() => {
    loadData();
    // Обновляем каждые 10 секунд для курса и каждые 30 секунд для остального
    const rateInterval = setInterval(loadExchangeRate, 10000); // Курс каждые 10 секунд
    const dataInterval = setInterval(loadData, 30000); // Остальное каждые 30 секунд
    return () => {
      clearInterval(rateInterval);
      clearInterval(dataInterval);
    };
  }, []);

  // Отдельная функция для загрузки курса
  const loadExchangeRate = async () => {
    setRateLoading(true);
    try {
      const rateRes = await api.get('/exchange/rate');
      let newRate = null;
      
      // Проверяем структуру ответа
      if (rateRes.data.success && rateRes.data.rate) {
        newRate = rateRes.data.rate;
      } else if (rateRes.data.tonToSperm) {
        // Fallback для старого формата
        newRate = rateRes.data;
      }
      
      if (newRate) {
        // Сохраняем предыдущий курс для сравнения
        if (exchangeRate) {
          setPrevRate(exchangeRate.tonToSperm);
        }
        setExchangeRate(newRate);
        
        // Показываем индикацию обновления
        setRateUpdated(true);
        setTimeout(() => setRateUpdated(false), 1000);
      }
    } catch (err) {
      console.error('Ошибка загрузки курса:', err);
    } finally {
      setRateLoading(false);
    }
  };

  const loadData = async () => {
    try {
      // Загружаем депозиты
      const depositsRes = await api.get('/admin/deposits');
      setDeposits(depositsRes.data.deposits || []);
      setPendingDeposits(depositsRes.data.deposits?.filter(d => d.status === 'pending') || []);
      
      // Загружаем выводы  
      const withdrawalsRes = await api.get('/admin/withdrawals');
      setWithdrawals(withdrawalsRes.data.withdrawals || []);
      setPendingWithdrawals(withdrawalsRes.data.withdrawals?.filter(w => w.status === 'pending') || []);
      
      // Загружаем курс обмена
      await loadExchangeRate();
      
      // Загружаем информацию о пуле
      const poolRes = await api.get('/exchange/pool-info');
      setPoolInfo(poolRes.data.pool);
      
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const processDeposit = async (depositId) => {
    try {
      setLoading(true);
      await api.post(`/admin/deposits/${depositId}/process`);
      setMessage('Депозит обработан успешно');
      loadData();
    } catch (err) {
      setError('Ошибка обработки депозита');
    } finally {
      setLoading(false);
    }
  };

  const approveWithdrawal = async () => {
    if (!selectedWithdrawal) return;
    
    try {
      setLoading(true);
      await api.post(`/admin/withdrawals/${selectedWithdrawal.id}/approve`);
      setMessage('Вывод одобрен и отправлен');
      setWithdrawalDialog(false);
      setSelectedWithdrawal(null);
      loadData();
    } catch (err) {
      setError('Ошибка обработки вывода');
    } finally {
      setLoading(false);
    }
  };

  const rejectWithdrawal = async (withdrawalId, reason) => {
    try {
      setLoading(true);
      await api.post(`/admin/withdrawals/${withdrawalId}/reject`, { reason });
      setMessage('Вывод отклонен');
      loadData();
    } catch (err) {
      setError('Ошибка отклонения вывода');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async () => {
    try {
      setLoading(true);
      await api.post('/admin/ton-settings', settings);
      setMessage('Настройки сохранены');
    } catch (err) {
      setError('Ошибка сохранения настроек');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <Pending /> },
      processing: { color: 'info', icon: <SwapHoriz /> },
      confirmed: { color: 'success', icon: <Check /> },
      completed: { color: 'success', icon: <Check /> },
      failed: { color: 'error', icon: <Error /> },
      cancelled: { color: 'default', icon: <Error /> }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Chip 
        label={status} 
        color={config.color} 
        size="small" 
        icon={config.icon}
      />
    );
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>
        TON Интеграция
      </Typography>
      
      {message && <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      
      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Курс обмена
                  </Typography>
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Fade in={!rateLoading}>
                        <Typography variant="h5" color={rateUpdated ? 'primary' : 'textPrimary'}>
                          1 SPERM = {exchangeRate?.spermPriceUSD ? exchangeRate.spermPriceUSD.toFixed(3) : '0.036'} USDT
                        </Typography>
                      </Fade>
                    {rateLoading && (
                      <CircularProgress size={20} sx={{ ml: 1 }} />
                    )}
                    </Box>
                    <Typography variant="caption" color="textSecondary" mt={0.5}>
                      {exchangeRate?.source === 'stonfi' ? '💱 STON.fi' : 
                       exchangeRate?.source === 'calculated' ? '💱 Расчетный' :
                       exchangeRate?.source === 'cached' ? '💱 Расчетный' : '💱 Расчетный'}
                    </Typography>
                  </Box>
                  {exchangeRate?.timestamp && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      Обновлено: {new Date(exchangeRate.timestamp).toLocaleTimeString('ru-RU')}
                    </Typography>
                  )}
                </Box>
                <SwapHoriz color={rateUpdated ? 'primary' : 'action'} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Ожидающие депозиты
                  </Typography>
                  <Typography variant="h5">
                    {pendingDeposits.length}
                  </Typography>
                </Box>
                <AccountBalanceWallet color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Ожидающие выводы
                  </Typography>
                  <Typography variant="h5">
                    {pendingWithdrawals.length}
                  </Typography>
                </Box>
                <Send color="error" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    TVL пула
                  </Typography>
                  <Typography variant="h5">
                    {poolInfo?.exists ? `$${(poolInfo.tvlUsd || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}` : 'Н/Д'}
                  </Typography>
                  {poolInfo?.exists && (
                    <Typography variant="caption" color="textSecondary">
                      ≈ {((poolInfo.tonReserve / 1_000_000_000) * 2).toLocaleString('ru-RU', {maximumFractionDigits: 2})} TON
                    </Typography>
                  )}
                </Box>
                <AccountBalanceWallet color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Табы */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="Депозиты" />
          <Tab label="Выводы" />
          <Tab label="Курсы обмена" />
        </Tabs>
        
        {/* Депозиты */}
        <TabPanel value={tab} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Последние депозиты</Typography>
            <Button 
              startIcon={<Refresh />} 
              onClick={loadData}
              disabled={loading}
            >
              Обновить
            </Button>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Сумма</TableCell>
                  <TableCell>От адреса</TableCell>
                  <TableCell>TX Hash</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deposits.map((deposit) => (
                  <TableRow key={deposit.id}>
                    <TableCell>{deposit.id}</TableCell>
                    <TableCell>{deposit.username}</TableCell>
                    <TableCell>{deposit.amount} SPERM</TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                        {deposit.fromAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                        {deposit.txHash || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(deposit.status)}</TableCell>
                    <TableCell>
                      {new Date(deposit.createdAt).toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell>
                      {deposit.status === 'pending' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => processDeposit(deposit.id)}
                          disabled={loading}
                        >
                          Обработать
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* Выводы */}
        <TabPanel value={tab} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Запросы на вывод</Typography>
            <Button 
              startIcon={<Refresh />} 
              onClick={loadData}
              disabled={loading}
            >
              Обновить
            </Button>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Сумма</TableCell>
                  <TableCell>На адрес</TableCell>
                  <TableCell>Комиссия</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.id}>
                    <TableCell>{withdrawal.id}</TableCell>
                    <TableCell>{withdrawal.username}</TableCell>
                    <TableCell>{withdrawal.amount} SPERM</TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                        {withdrawal.toAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>{withdrawal.fee} SPERM</TableCell>
                    <TableCell>{getStatusChip(withdrawal.status)}</TableCell>
                    <TableCell>
                      {new Date(withdrawal.createdAt).toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell>
                      {withdrawal.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => {
                              setSelectedWithdrawal(withdrawal);
                              setWithdrawalDialog(true);
                            }}
                            disabled={loading}
                            sx={{ mr: 1 }}
                          >
                            Одобрить
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => rejectWithdrawal(withdrawal.id, 'Отклонено администратором')}
                            disabled={loading}
                          >
                            Отклонить
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* Курсы обмена */}
        <TabPanel value={tab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                      Текущий курс
                    </Typography>
                    {rateLoading && <CircularProgress size={20} />}
                  </Box>
                  <Box sx={{ mt: 2, position: 'relative' }}>
                    <Fade in={rateUpdated} timeout={500}>
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: -10, 
                          right: -10, 
                          bgcolor: 'primary.main',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}
                      >
                        ОБНОВЛЕНО
                      </Box>
                    </Fade>
                    <Typography variant="h5" color={rateUpdated ? 'primary' : 'textPrimary'}>
                      1 TON = {exchangeRate?.tonToSperm ? exchangeRate.tonToSperm.toLocaleString('ru-RU') : '...'} SPERM
                    </Typography>
                    <Typography variant="h6" color="textSecondary" mt={1}>
                      1 SPERM = {exchangeRate?.spermToTon ? exchangeRate.spermToTon.toFixed(6) : '...'} TON
                    </Typography>
                    <Box mt={2}>
                      <Chip 
                        label={exchangeRate?.source === 'stonfi' ? '📊 STON.fi DEX' : 
                               exchangeRate?.source === 'calculated' ? '💱 Расчетный курс (USD)' :
                               exchangeRate?.source === 'fixed' ? '📌 Фиксированный курс' : 'N/A'} 
                        color={exchangeRate?.source === 'calculated' ? 'primary' : 
                               exchangeRate?.source === 'stonfi' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    {exchangeRate?.tonPriceUSD && exchangeRate?.spermPriceUSD && (
                      <Box mt={2} p={2} bgcolor="background.paper" borderRadius={1}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          💵 Курсы USD:
                        </Typography>
                        <Typography variant="body2">
                          1 TON = ${exchangeRate.tonPriceUSD?.toFixed(2)}
                        </Typography>
                        <Typography variant="h6" color="primary" fontWeight="bold" mt={1}>
                          💰 1 SPERM = $0.036
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Актуальный курс монеты
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" display="block" color="textSecondary" mt={1}>
                      ⏱️ Обновляется каждые 10 секунд
                    </Typography>
                    {exchangeRate?.timestamp && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        Последнее обновление: {new Date(exchangeRate.timestamp).toLocaleString('ru-RU')}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Информация о пуле
                  </Typography>
                  {poolInfo?.exists ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        💰 Резервы пула:
                      </Typography>
                      <Typography variant="body2">
                        • SPERM: {(poolInfo.spermReserve / 1_000_000_000).toLocaleString('ru-RU', {maximumFractionDigits: 2})} SPERM
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • TON: {(poolInfo.tonReserve / 1_000_000_000).toLocaleString('ru-RU', {maximumFractionDigits: 2})} TON
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        📊 TVL: ${(poolInfo.tvlUsd || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}
                      </Typography>
                      <Typography variant="body2">
                        📈 APY (7д): {poolInfo.apy}%
                      </Typography>
                      <Typography variant="body2">
                        📊 Объем 24ч: ${(poolInfo.volume24h || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="textSecondary">
                      Пул не найден на STON.fi
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      {/* Диалог подтверждения вывода */}
      <Dialog open={withdrawalDialog} onClose={() => setWithdrawalDialog(false)}>
        <DialogTitle>Подтверждение вывода</DialogTitle>
        <DialogContent>
          {selectedWithdrawal && (
            <Box>
              <Typography>
                Вы уверены, что хотите одобрить вывод?
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Пользователь: {selectedWithdrawal.username}
                </Typography>
                <Typography variant="body2">
                  Сумма: {selectedWithdrawal.amount} SPERM
                </Typography>
                <Typography variant="body2">
                  На адрес: {selectedWithdrawal.toAddress}
                </Typography>
                <Typography variant="body2">
                  Комиссия: {selectedWithdrawal.fee} SPERM
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalDialog(false)}>
            Отмена
          </Button>
          <Button 
            onClick={approveWithdrawal}
            variant="contained"
            color="success"
            disabled={loading}
          >
            Одобрить и отправить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TonIntegration;